<template>
  <!-- "Task details" from the Work Progress design: one row per leaf task in scope, with search
       and a CSV download. Same status rules and figures as the cards above it. -->
  <v-card class="details-card mb-4">
    <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-2">
      <span class="card-title">{{ $t('taskDetails.title') }}</span>
      <div class="d-flex align-center">
        <v-text-field
          v-model="search"
          :label="$t('search')"
          hide-details
          density="compact"
          variant="outlined"
          prepend-inner-icon="mdi-magnify"
          clearable
          class="mr-2"
          style="max-width: 320px; min-width: 200px;"
        />
        <v-btn icon variant="plain" :title="$t('taskDetails.download')" :disabled="!rows.length" @click="downloadCsv">
          <v-icon>mdi-download</v-icon>
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text class="pt-0">
      <v-skeleton-loader v-if="loading && !rows.length" type="table" />
      <v-data-table
        v-else
        :headers="headers"
        :items="rows"
        :search="search"
        :custom-filter="matches"
        :items-per-page="10"
        :locale="$i18n.locale"
        :no-data-text="$t('taskSCurve.noData')"
        :items-per-page-text="$t('itemsPerPage')"
        density="comfortable"
        class="elevation-0 details-table"
        :row-props="rowProps"
        @click:row="onRowClick"
      >
        <template #item.taskTitle="{ item }">
          <span class="font-weight-medium text-no-wrap">{{ item.taskTitle }}</span>
        </template>
        <template #item.url="{ item }">
          <v-btn
            v-if="item.url"
            icon
            variant="text"
            size="small"
            class="viewer-btn"
            :href="item.url"
            :title="$t('taskDetails.taskViewer')"
            :aria-label="$t('taskDetails.taskViewer')"
            @click.prevent.stop="openUrl(item.url)"
          >
            <v-icon size="20">mdi-clipboard-text</v-icon>
          </v-btn>
        </template>
        <template #item.planViews="{ item }">
          <!-- one location: open it; several: pick one from a menu -->
          <v-btn
            v-if="item.planViews.length === 1"
            icon
            variant="text"
            size="small"
            class="viewer-btn"
            :href="item.planViews[0].url"
            :title="`${$t('taskDetails.planViewer')} · ${item.planViews[0].title}`"
            :aria-label="$t('taskDetails.planViewer')"
            @click.prevent.stop="openUrl(item.planViews[0].url)"
          >
            <v-icon size="20">mdi-map</v-icon>
          </v-btn>
          <v-menu v-else-if="item.planViews.length > 1">
            <template #activator="{ props: menuProps }">
              <v-btn
                v-bind="menuProps"
                icon
                variant="text"
                size="small"
                class="viewer-btn"
                :title="$t('taskDetails.planViewer')"
                :aria-label="$t('taskDetails.planViewer')"
                @click.stop
              >
                <v-icon size="20">mdi-map</v-icon>
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item
                v-for="plan in item.planViews"
                :key="plan.url"
                :title="plan.title"
                prepend-icon="mdi-map-marker-outline"
                @click.stop="openUrl(plan.url)"
              />
            </v-list>
          </v-menu>
        </template>
        <template #item.path="{ item }">
          <span class="path">{{ item.path }}</span>
        </template>
        <template #item.locationTitles="{ item }">
          <span class="path" :title="item.locationTitles">{{ item.locationTitles }}</span>
        </template>
        <template #item.status="{ item }">
          <v-chip size="small" variant="tonal" :color="item.statusColor">{{ item.statusLabel }}</v-chip>
        </template>
        <template #item.actualPct="{ item }">{{ pct(item.actualPct) }}</template>
        <template #item.plannedPct="{ item }">{{ pct(item.plannedPct) }}</template>
        <template #item.plannedStartDate="{ item }"><span class="text-no-wrap">{{ date(item.plannedStartDate) }}</span></template>
        <template #item.actualStartDate="{ item }"><span class="text-no-wrap">{{ date(item.actualStartDate) }}</span></template>
        <template #item.plannedEndDate="{ item }"><span class="text-no-wrap">{{ date(item.plannedEndDate) }}</span></template>
        <template #item.completionDate="{ item }"><span class="text-no-wrap">{{ date(item.completionDate) }}</span></template>
        <template #item.overdueDays="{ item }">
          <span :class="item.overdueDays > 0 ? 'tk-err' : ''">{{ item.overdueDays }}</span>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { taskDetailsSql } from '@/utils/taskTables';
import { taskSCurveFilters, taskScopeKey, taskScope, isTaskScopeReady, toggleDetailsTaskFilter } from '@/utils/taskSCurveFilters';
import { formatShortDate } from '@/utils/taskDates';

const store = useStore();
const { t } = useI18n();
const conn = computed(() => store.getters.getDBConnection);
const dataLoaded = computed(() => store.getters.getDataLoaded);
const locale = computed(() => store.getters.currentLocale);
const projectCode = computed(() => store.getters.getProjectCode);
const settings = computed(() => store.state.settings);
const buildMode = computed(() => store.state.buildMode);
const { proxy } = getCurrentInstance();

// Same host rule as the issue links on the Project tab: the api- prefix is dropped from apiUrl
const siteHost = computed(() => {
  const api = settings.value?.apiUrl ?? '';
  return api.startsWith('api-') ? api.slice(4) : api;
});

// Task viewer URL, as the Power BI report builds it:
//   https://{host}/{projectCode}/task-viewer/{scheduleId}/@{timeRangeTemplateId},tree-grid,view#view/task{taskId}
const taskUrl = (r) => {
  if (!siteHost.value || !projectCode.value || !r.scheduleId || !r.timeRangeTemplateId || !r.taskId) return '';
  return `https://${siteHost.value}/${projectCode.value}/task-viewer/${r.scheduleId}/@${r.timeRangeTemplateId},tree-grid,view#view/task${r.taskId}`;
};

const openUrl = (url) => {
  if (buildMode.value === 'production') {
    proxy.$launchUrl(url);
  } else {
    window.open(url, '_blank');
  }
};

const raw = ref([]);
const loading = ref(false);
const search = ref('');
const scheduleMode = ref('sync');
const isLite = computed(() => scheduleMode.value === 'lite');

let queryVersion = 0;

const queryRows = async (query) => {
  const result = await conn.value.query(query);
  return result.toArray().map(row => row.toJSON());
};

const loadData = async () => {
  if (!dataLoaded.value || !isTaskScopeReady.value) return;
  const version = ++queryVersion;
  loading.value = true;
  try {
    // Ignore the details cross-filter here: this grid is where it is picked
    const result = await queryRows(taskDetailsSql({
      ...taskScope(['details']),
    }));
    if (version !== queryVersion) return; // a newer request superseded this one
    raw.value = result;
    if (result.length) scheduleMode.value = result[0].scheduleMode ?? 'sync';
  } catch (error) {
    if (version !== queryVersion) return;
    console.error('[TaskDetailsGrid] query failed:', error.message);
    raw.value = [];
  } finally {
    if (version === queryVersion) loading.value = false;
  }
};

const STATUS = {
  overdue: { key: 'overdue', color: 'error' },
  behindSchedule: { key: 'behindSchedule', color: 'warning' },
  onSchedule: { key: 'onSchedule', color: 'success' },
  inProgress: { key: 'inProgress', color: 'info' },
  upcoming: { key: 'upcoming', color: undefined },
  notStarted: { key: 'notStarted', color: undefined },
  completed: { key: 'completed', color: 'info' },
};

const num = v => (v === null || v === undefined ? null : Number(v));

// planViews arrives as a JSON string: [{ title, url }, …]
const parsePlanViews = (value) => {
  if (!value) return [];
  try {
    const list = typeof value === 'string' ? JSON.parse(value) : value;
    return Array.isArray(list) ? list.filter(p => p && p.url) : [];
  } catch {
    return [];
  }
};
const pct = v => (v === null ? '–' : v.toFixed(2));
const date = v => (v ? formatShortDate(v, locale.value) : '');

const rows = computed(() => raw.value.map((r) => {
  const status = STATUS[r.status] ?? STATUS.upcoming;
  return {
    taskId: r.taskId,
    url: taskUrl(r),
    taskTitle: r.taskTitle ?? '',
    path: r.path ?? '',
    locationTitles: r.locationTitles ?? '',
    planViews: parsePlanViews(r.planViews),
    status: r.status,
    statusLabel: t(`taskStatus.${status.key}`),
    statusColor: status.color,
    actualPct: num(r.actualPct) ?? 0,
    plannedPct: isLite.value ? null : num(r.plannedPct),
    plannedStartDate: r.plannedStartDate ?? '',
    actualStartDate: r.actualStartDate ?? '',
    plannedEndDate: r.plannedEndDate ?? '',
    completionDate: r.completionDate ?? '',
    overdueDays: num(r.overdueDays) ?? 0,
    cycleDays: num(r.cycleDays) ?? 0,
    statusOrder: num(r.statusOrder) ?? 9,
  };
}));

// Clicking a row cross-filters the other visuals to that task; the row is marked, the rest dim
const selected = computed(() => taskSCurveFilters.detailsTaskIds[0] ?? null);
const onRowClick = (event, { item }) => toggleDetailsTaskFilter(item.taskId);
const rowProps = ({ item }) => ({
  class: {
    'row--clickable': true,
    'is-selected': selected.value === item.taskId,
    'is-dimmed': selected.value !== null && selected.value !== item.taskId,
  },
  'aria-pressed': selected.value === item.taskId,
});

// Planned-date columns only exist on a dated (sync) schedule
const headers = computed(() => {
  const col = (key, titleKey, extra = {}) => ({ key, title: t(`taskDetails.${titleKey}`), ...extra });
  const numCol = (key, titleKey) => col(key, titleKey, { align: 'end' });
  const all = [
    col('url', 'taskViewer', { sortable: false, align: 'center', width: 90 }),
    col('planViews', 'planViewer', { sortable: false, align: 'center', width: 90 }),
    col('taskTitle', 'taskTitle'),
    col('path', 'hierarchyPath'),
    col('locationTitles', 'location'),
    col('status', 'status', { sortRaw: (a, b) => a.statusOrder - b.statusOrder }),
    numCol('actualPct', 'actualProgress'),
    numCol('plannedPct', 'plannedProgress'),
    col('plannedStartDate', 'plannedStart'),
    col('actualStartDate', 'actualStart'),
    col('plannedEndDate', 'plannedEnd'),
    col('completionDate', 'completion'),
    numCol('overdueDays', 'overdueDays'),
    numCol('cycleDays', 'cycleDays'),
  ];
  const liteHidden = ['plannedPct', 'plannedStartDate', 'plannedEndDate', 'overdueDays'];
  return isLite.value ? all.filter(h => !liteHidden.includes(h.key)) : all;
});

// Search on title, path and status label only (the default filter would also match raw dates / ids)
const matches = (value, query, item) => {
  if (!query) return true;
  const q = String(query).toLowerCase();
  const r = item?.raw ?? item;
  return [r.taskTitle, r.path, r.locationTitles, r.statusLabel].some(v => String(v ?? '').toLowerCase().includes(q));
};

const downloadCsv = () => {
  // The Task viewer column exports as the task's URL
  const cols = headers.value;
  const cell = (row, key) => {
    switch (key) {
      case 'status': return row.statusLabel;
      case 'planViews': return row.planViews.map(p => p.url).join(' | ');
      case 'actualPct':
      case 'plannedPct': return pct(row[key]);
      case 'plannedStartDate':
      case 'actualStartDate':
      case 'plannedEndDate':
      case 'completionDate': return date(row[key]);
      default: return row[key];
    }
  };
  const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [
    cols.map(c => escape(c.title)).join(','),
    ...rows.value.map(row => cols.map(c => escape(cell(row, c.key))).join(',')),
  ];
  // BOM so Excel reads the UTF-8 titles correctly
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${projectCode.value || 'project'}-task-details.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

onMounted(loadData);
watch(
  [dataLoaded, taskScopeKey],
  loadData,
  { deep: true }
);
</script>

<style scoped>
.details-card {
  width: 100%;
  margin: 0 0 16px; /* App.vue gives every v-card margin: 0 auto */
}
.card-title {
  font-size: 20px;
  line-height: 28px;
  font-weight: 400;
}
.viewer-btn {
  opacity: 0.7;
}
.viewer-btn:hover {
  opacity: 1;
}
.path {
  font-size: 12px;
  opacity: 0.7;
  display: inline-block;
  max-width: 260px;
}
.details-table :deep(th) {
  white-space: nowrap;
}
.details-table :deep(td) {
  font-variant-numeric: tabular-nums;
}
.details-table :deep(tr.row--clickable) {
  cursor: pointer;
  transition: opacity 0.2s ease;
}
.details-table :deep(tr.is-selected td) {
  background: var(--tk-row-hover);
  box-shadow: inset 3px 0 0 var(--tk-info);
}
.details-table :deep(tr.is-dimmed) {
  opacity: 0.45;
}
</style>
